import { theme } from 'components/Theme'
import { IOaiUser, IUser } from 'features/auth/types'
import { ASSIGN_UNASSIGN, getSelectedMenu, INITIAL_PAGINATION, UNASSIGNED, USER_LIMIT } from 'features/claims'
import { updateClaimEntityBag } from 'features/claims/claimsApi'
import { ClaimOwnerAvatar } from 'features/claims/components/ClaimCommon'
import { getRemarksOwnership } from 'features/claims/components/ClaimsTable/Internal/BulkActionConfirmationModal'
import { getLongAgentName, getUserListQuery } from 'features/claims/utils'
import { WellKnownActorsEnum, wellKnownEntityIDs } from 'features/entities/types'
import { useRevalidateAllSwrCache } from 'hooks/useRevalidateAllSwrCache'
import { useState } from 'react'
import { FaTimesCircle, FaUserAlt } from 'react-icons/fa'
import { Avatar } from '@outbound-ai/ursa'
import { DropdownWithUserSearch } from 'components/DropdownWithUserSearch/DropdownWithUserSearch'
import { OUTBOUND_SUPPORT } from 'utils/constants'
import { useDebounces } from 'utils/hooks'
import { httpTenantsService } from 'utils/http'
import { notificator } from 'utils/notificator/notificator'
import storage, { StorageEnum } from 'utils/storage'
import { getAvailableUsername, getEmptyState, getUserDetail } from '../../ClaimsTable/Internal/BulkAssignAndUnAssign'

interface IProps {
  claimOwner: string
  claimOwnerId: string
  visitId: string
  oaiClaimId: string
}

export const AssignAndUnAssign = ({ claimOwner, claimOwnerId, visitId, oaiClaimId }: IProps) => {
  const currentUser: IUser = storage.get(StorageEnum.currentUser)

  const [fullUserList, setFullUserList] = useState<IOaiUser[]>([]) // Original list
  const [userList, setUserList] = useState<IOaiUser[]>([]) // Filtered list

  const [userListPagination, setUserListPagination] = useState(INITIAL_PAGINATION)
  const [hasMoreUsers, setHasMoreUsers] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  const revalidateAllSwrCache = useRevalidateAllSwrCache()

  const getUserList = (text?: string, offset?: number) => {
    const { isSearch, updatedUrl } = getUserListQuery(offset == 0 ? 0 : userListPagination.offset, text)
    setDataUserList(updatedUrl, isSearch)
  }

  const setDataUserList = (url: string, isSearch: boolean) => {
    httpTenantsService
      .get(url)
      .then((res) => {
        const users = res.data
        if (users) {
          const updateUserList = isSearch ? [] : [...fullUserList]
          const filteredUsers = [...updateUserList, ...users.data]
          const removeDuplicates = filteredUsers.filter(
            (obj, index, self) => index === self.findIndex((t: IOaiUser) => t.userGuid === obj.userGuid),
          )
          setFullUserList(removeDuplicates) // Update original list
          setUserList(removeDuplicates) // Update filtered list
          if (users.data.length === 0 || users.data.length < USER_LIMIT) {
            setHasMoreUsers(false)
            return
          } else {
            setHasMoreUsers(true)
          }
          setUserListPagination({ count: users.count, limit: users.limit, offset: users.offset + USER_LIMIT })
        }
      })
      .catch(() => {
        notificator.notify({
          type: 'error',
          heading: 'Something went wrong',
          message: (
            <>
              Please refresh the page and try again. If you’re still <br /> having issues, contact{' '}
              <span className="text-decoration-underline" onClick={openEmail}>
                {' '}
                Customer Support
              </span>
              .
            </>
          ),
          toastOptions: {
            icon: <FaTimesCircle />,
            closeButton: true,
          },
        })
        setHasMoreUsers(false)
      })
  }

  const openEmail = () => {
    window.open(`mailto:${OUTBOUND_SUPPORT}?subject=&body=`)
  }

  const userSearchByName = useDebounces((text: string) => {
    if (text?.length <= 0) {
      setUserList(fullUserList)
    } else {
      const searchText = text.toLowerCase()
      const filteredUsers = fullUserList.filter((user) => {
        const availableName = getAvailableUsername(user).toLowerCase()
        const username = user.username.toLowerCase()
        return availableName.includes(searchText) || username.includes(searchText)
      })
      setUserList(filteredUsers)
    }
  }, 800)

  const dropdownInitLoad = () => {
    setHasMoreUsers(true)
    getUserList()
  }

  const dropdownClosed = () => {
    setUserListPagination({ ...userListPagination, count: 0, offset: 0, limit: 0 })
    setUserList([])
  }

  const handleSelectedUser = (user: IOaiUser | ASSIGN_UNASSIGN) => {
    const { userName, userId } = getUserDetail(user, currentUser)
    handleTakeOwnership(userName, userId!)
  }

  const handleTakeOwnership = (userName: string | null, userId: string | null) => {
    setIsLoading(true)
    const isUnassign = (userName && userId) === null ? true : false

    const actorName = getLongAgentName(currentUser)
    const assignOwnerData: any = prepareEntityBag(actorName, userName, userId, claimOwner)

    updateClaimEntityBag(oaiClaimId, assignOwnerData)
      .then(() => {
        revalidateAllSwrCache()
        notificator.notify({
          heading: `${visitId} has been ${isUnassign ? 'unassigned' : 'assigned'} `,
          type: 'success',
        })
      })
      .catch((error) => {
        const message = error.response?.data?.detail?.toLocaleLowerCase()
        const isCallInProgress =
          message === 'call is queued or in progress on claim, cannot take ownership until call is complete.'
        if (error.response?.data?.status === 409 && isCallInProgress) {
          notificator.notify({
            heading: 'Claim assignment available after call is complete',
            message: 'There is a call in progress. You can assign claim ownership after the call has completed.',
            type: 'info',
          })
        } else {
          notificator.notify({
            heading: error.response?.data?.detail || error.message,
            type: 'error',
          })
        }
      })

      .finally(() => setIsLoading(false))
  }

  return (
    userList && (
      <DropdownWithUserSearch
        variant="secondary"
        isLoading={isLoading}
        label={claimOwner || UNASSIGNED}
        labelCount={{ Min: 1, Total: 19 }}
        items={userList.map((user: IOaiUser) => ({
          userName: getAvailableUsername(user),
          onClick: () => handleSelectedUser(user),
        }))}
        searchText={(t: any) => {
          userSearchByName(t)
        }}
        initialLoad={dropdownInitLoad}
        close={dropdownClosed}
        emptyState={getEmptyState(hasMoreUsers)}
        icon={
          claimOwner ? (
            <ClaimOwnerAvatar claimOwner={claimOwner} />
          ) : (
            <Avatar
              backgroundColor={theme.colors.grey.light}
              color={theme.colors.grey.lightest}
              size="small"
              initials={<FaUserAlt />}
            />
          )
        }
        dataLength={userListPagination.offset}
        next={(t: string) => getUserList(t)}
        hasMore={hasMoreUsers}
        assignUnAssign={(value: ASSIGN_UNASSIGN) => handleSelectedUser(value)}
        selectedMenu={getSelectedMenu(currentUser.id!, claimOwnerId)}
      />
    )
  )
}

function prepareEntityBag(actorName: string, userName: string | null, userId: string | null, currentOwner: string) {
  return {
    entities: {
      [wellKnownEntityIDs.ownerName]: userName || UNASSIGNED,
      [wellKnownEntityIDs.ownerId]: userId || null,
    },
    actorId: WellKnownActorsEnum.rcmAgent,
    actorName: actorName,
    activityId: '3dca2d43-aaf1-4e36-8636-22c706d66146',
    remarks: getRemarksOwnership(currentOwner, actorName, userName ? userName : UNASSIGNED),
    predecessorId: null,
  }
}
